import React, { useEffect, useState } from "react";

import MenuItem from "@mui/material/MenuItem";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import { colors } from "@zapalacorp/tokens";
import { Select } from "Components/Form";
import { useTranslation } from "react-i18next";
import { SelectChangeEvent } from "@mui/material";

interface IPaginationConfig {
  total: number;
  pageSizeOptions?: number[];
  currentPage?: number;
  changePage?: (page: number) => void | Function;
  pageSize?: number;
  changePageSize?: (pageSize: number) => void | Function;
}
const arraySizeOptions = [20, 40, 60, 100];
const PaginationComponent = (props: IPaginationConfig) => {
  const [selectPageOption, setSelectPageOption] = useState<number>(0);
  const [pageOptionLocal, setPageOptionLocal] = useState<number[]>(
    props?.pageSizeOptions || arraySizeOptions
  );
  const [start, setStart] = useState<number>(1);
  const [end, setEnd] = useState<number>(Math.min(selectPageOption, props?.total));
  const { t: translate } = useTranslation();
  const [page, setPage] = useState(props?.currentPage || 1);
  const [count, setCount] = useState(1);
  const calculateTotal = (value: number) => {
    if (!value) return 0;

    return Math.ceil(value / selectPageOption) || 0;
  };

  useEffect(() => {
    const newStart = (page - 1) * selectPageOption + 1;
    const newEnd = Math.min(newStart + selectPageOption - 1, props.total);

    setStart(newStart);
    setEnd(newEnd);
    setCount(calculateTotal(props?.total));
  }, [page, selectPageOption, props.total]);

  useEffect(() => {
    if (props?.pageSize) {
      setSelectPageOption(props?.pageSize);
    }
  }, [props?.pageSize]);

  useEffect(() => {
    if (props?.pageSizeOptions) {
      setPageOptionLocal(props?.pageSizeOptions);
    }
  }, [props?.pageSizeOptions]);

  return (
    <>
      {props?.total > 0 && (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <div
              style={{
                color: colors.gray600,
                fontSize: "12px",
                fontStyle: "normal",
                fontFamily: "Manrope",
                fontWeight: 600,
                lineHeight: "16px",
              }}
            >
              <span
                style={{
                  marginRight: "4px",
                }}
              >
                {translate("showing")}
              </span>
              <span>{start}</span>
              <span> - </span>
              <span>{end}</span>
              <span> {translate("of")} </span>
              <span
                style={{
                  fontWeight: 700,
                  color: colors.gray800,
                }}
              >
                {props.total}
              </span>
              <span> {translate("items2")} </span>
              <span
                style={{
                  marginLeft: "6px",
                  marginRight: "6px",
                }}
              >
                |
              </span>
              <span>{translate("display")}</span>
            </div>
            <div
              style={{
                display: "flex",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <Select
                style={{
                  minWidth: "130px",
                }}
                value={selectPageOption}
                data-cy="mode"
                onChange={(e: SelectChangeEvent<any>) => {
                  setSelectPageOption(parseInt(e.target.value, 10));
                  setPage(1);
                  if (typeof props?.changePageSize === "function") {
                    props?.changePageSize(parseInt(e.target.value, 10));
                  }
                }}
                displayEmpty
              >
                {pageOptionLocal.map((item) => (
                  <MenuItem key={item} value={item} data-cy={`mode-value-${item}`}>
                    <div
                      style={{
                        color: colors.brandBlue500,
                      }}
                    >
                      {item}
                    </div>
                  </MenuItem>
                ))}
              </Select>
              <div
                style={{
                  color: colors.gray600,
                  fontSize: "12px",
                  fontStyle: "normal",
                  fontFamily: "Manrope",
                  fontWeight: 600,
                  lineHeight: "16px",
                }}
              >
                {translate("items2")}
              </div>
            </div>
          </div>
          <Pagination
            onChange={(_, value) => {
              setPage(value);
              if (typeof props?.changePage === "function") {
                props.changePage(value);
              }
            }}
            page={page}
            count={count} // Ensure correct total pages
            renderItem={(item) => (
              <PaginationItem
                slots={{ previous: ArrowBackIosIcon, next: ArrowForwardIosIcon }}
                {...item}
                sx={{
                  color: item.selected ? "#308CDB" : "#667085",
                  fontStyle: "normal",
                  textAlign: "center",
                  fontFamily: "Manrope", // Ensure custom font
                  fontWeight: 600,
                  lineHeight: "24px",
                  letterSpacing: "0em",
                  backgroundColor: "#ffffff",
                  borderRadius: "0",

                  borderLeft: "none !important",
                  "&.Mui-selected": {
                    backgroundColor: "#ffffff !important", // ✅ Ensure white background on selected
                    fontWeight: 700, // Optional: Make selected item bold
                  },
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                  ...(item.type === "previous" && {
                    borderLeft: "1px solid #e4e7ec !important",
                    borderRadius: "0 !important",
                    borderTopLeftRadius: "10px !important",
                    borderBottomLeftRadius: "10px !important",
                  }),
                  ...(item.type === "next" && {
                    borderRadius: "0 !important",
                    borderTopRightRadius: "10px !important",
                    borderBottomRightRadius: "10px !important",
                  }),
                  "& svg": {
                    width: "20px", // ✅ Custom icon width
                    height: "20px", // ✅ Custom icon height
                  },
                }}
              />
            )}
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: "20px",
              "& .MuiPaginationItem-root": {
                minWidth: "44px",
                minHeight: "44px",
                border: "1px solid #D1D5DB",
                borderRadius: "0px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0px",
                borderTop: "1px solid #e4e7ec !important",
                borderRight: "1px solid #e4e7ec !important",
                borderBottom: "1px solid #e4e7ec !important",
                borderLeft: "none",
              },
              "& .MuiPaginationItem-root.Mui-selected": {
                backgroundColor: "white !important", // ✅ Ensure white background on selected
              },
              "& .MuiPaginationItem-root.Mui-disabled": {
                opacity: 1, // ✅ Ensures previous & next buttons stay visible when disabled
              },
            }}
          />
        </div>
      )}
    </>
  );
};

export default PaginationComponent;
